import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Loading } from '../common/Loading';
import { useMessage } from '../../contexts/MessageContext';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const inputCls =
  'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
const sectionTitle = 'text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const EnquiryView = () => {
  const [enquiry, setenquiry] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    eventType: '',
    eventDate: new Date().toISOString().split('T')[0],
    status: '',
    message: '',
  });
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useMessage();
  const { uuid } = useParams();
  const isEditMode = !!uuid;

  const eventTypes = [
    { value: 'Birthday', label: 'Birthday' },
    { value: 'Baby_Shower', label: 'Baby Shower' },
    { value: 'Wedding', label: 'Wedding' },
    { value: 'Corporate', label: 'Corporate' },
    { value: 'Other', label: 'Other' },
  ];

  const eventStatus = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'quoted', label: 'Quoted' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      logout();
      navigate('/login');
      return;
    }

    if (isEditMode) {
      const fetchEnquery = async () => {
        try {
          const response = await axios.get(`${apiUrl}/enquiry/${uuid}`, {
            headers: { 'x-auth-token': token },
          });
          const data = response.data;
          setenquiry({
            ...data,
            eventDate: data.eventDate ? data.eventDate.split('T')[0] : '',
          });
        } catch (error) {
          console.error('Error fetching enquiry:', error);
          if (error.response && error.response.status === 401) {
            showError('Unauthorized. Please login again.');
            logout();
            navigate('/login');
            return;
          }
          showError('Failed to fetch enquiry');
        } finally {
          setLoading(false);
        }
      };

      fetchEnquery();
    } else {
      setLoading(false);
    }
  }, [logout, navigate, isEditMode, uuid, showError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (isEditMode) {
        await axios.put(`${apiUrl}/enquiry/${uuid}`, enquiry, {
          headers: { 'x-auth-token': token },
        });
        showSuccess('Enquiry updated successfully!');
      } else {
        await axios.post(`${apiUrl}/enquiry`, enquiry, {
          headers: { 'x-auth-token': token },
        });
        showSuccess('Enquiry created successfully!');
        navigate('/enquiries');
        return;
      }
      navigate('/enquiries');
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      showError('Failed to submit enquiry');
    }
  };

  if (loading) {
    return <Loading text="Loading enquiry…" />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white m-5 p-6 rounded shadow-md"
    >
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-1">
          {isEditMode ? 'Edit Enquiry' : 'Create Enquiry'}
        </h2>
        <p className="text-sm text-gray-500">
          {isEditMode
            ? 'Update contact details, event information, and pipeline status.'
            : 'Add a new enquiry for follow-up and quoting.'}
        </p>
        {isEditMode && enquiry.createdAt && (
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-gray-500">Submitted:</span>{' '}
              <span className="font-medium text-gray-800">{formatDate(enquiry.createdAt)}</span>
            </div>
            {enquiry.quoteNumber && (
              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <span className="text-gray-500">Quote #:</span>{' '}
                <span className="font-medium text-gray-800">{enquiry.quoteNumber}</span>
              </div>
            )}
            {enquiry.customer && (
              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <span className="text-gray-500">Customer:</span>{' '}
                <span className="font-medium text-gray-800">{enquiry.customer.firstname} {enquiry.customer.lastname || ''}</span>
              </div>
            )}
            {enquiry.quoteId && (
              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <span className="text-gray-500">Draft Quote:</span>{' '}
                <button
                  type="button"
                  className="font-medium text-blue-600 hover:text-blue-800"
                  onClick={() => navigate(`/quotes/${enquiry.quoteId}`)}
                >
                  Open Quote →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className={sectionTitle}>Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Name *</label>
            <input
              type="text"
              className={inputCls}
              placeholder="Customer name"
              value={enquiry.customerName}
              onChange={(e) => setenquiry({ ...enquiry, customerName: e.target.value })}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Phone *</label>
            <input
              type="text"
              className={inputCls}
              value={enquiry.customerPhone || ''}
              onChange={(e) => setenquiry({ ...enquiry, customerPhone: e.target.value })}
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Email *</label>
            <input
              type="email"
              className={inputCls}
              value={enquiry.customerEmail || ''}
              onChange={(e) => setenquiry({ ...enquiry, customerEmail: e.target.value })}
              required
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className={sectionTitle}>Event</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Event date *</label>
            <input
              type="date"
              className={inputCls}
              value={enquiry.eventDate || ''}
              onChange={(e) => setenquiry({ ...enquiry, eventDate: e.target.value })}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Event type *</label>
            <select
              className={inputCls}
              value={enquiry.eventType}
              onChange={(e) => setenquiry({ ...enquiry, eventType: e.target.value })}
              required
            >
              <option value="">Select a type</option>
              {eventTypes.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Status *</label>
            <select
              className={inputCls}
              value={enquiry.status}
              onChange={(e) => setenquiry({ ...enquiry, status: e.target.value })}
              required
            >
              <option value="">Select status</option>
              {eventStatus.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className={sectionTitle}>Message</h3>
        <div>
          <label className={labelCls}>Details *</label>
          <textarea
            className={inputCls}
            rows={4}
            placeholder="Enquiry or notes from the customer"
            value={enquiry.message}
            onChange={(e) => setenquiry({ ...enquiry, message: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <button className="btn-primary" type="submit">
          {isEditMode ? 'Update Enquiry' : 'Submit Enquiry'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate('/enquiries')}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default EnquiryView;
